import api from './api';

/**
 * Photo Upload Service
 * Handles uploading photos to S3 via backend API with deduplication support
 */
class PhotoUploadService {
  /**
   * Calculate SHA-256 hash of a file
   * @param {File} file - File to hash
   * @returns {Promise<string>} Hex hash string
   */
  async calculateFileHash(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          resolve(hashHex);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Upload photo file to S3
   * @param {File} file - Photo file to upload
   * @returns {Promise<Object>} Upload result with S3 URL, hash, and deduplication info
   */
  async uploadPhoto(file) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }
      
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size exceeds 10MB limit');
      }
      
      // Calculate file hash for deduplication (optional - backend can also calculate)
      let fileHash = null;
      try {
        fileHash = await this.calculateFileHash(file);
      } catch (hashError) {
        console.warn('Failed to calculate file hash, continuing without it:', hashError);
        // Continue without hash - backend will calculate it
      }
      
      const formData = new FormData();
      formData.append('photo', file);
      if (fileHash) {
        formData.append('fileHash', fileHash); // Pass hash to backend for checking
      }

      console.log('Uploading photo:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        hasHash: !!fileHash
      });

      const response = await api.post('/ai-analyses/upload-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.error || 'Upload failed - no response data');
      }
      
      if (!response.data.data || !response.data.data.photoUrl) {
        throw new Error('Upload succeeded but no photo URL returned');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error uploading photo:', error);
      
      // Extract detailed error message
      let errorMessage = 'Failed to upload photo';
      
      if (error.response) {
        // Axios error with response
        const errorData = error.response.data;
        if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          errorMessage = `Upload failed: ${error.response.status} ${error.response.statusText}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Upload a media file (image or video) to S3. Same storage endpoint as
   * uploadPhoto, but allows the larger video size limit and a longer timeout.
   * Used for verification evidence where the verifier attaches a short video.
   * @param {File} file - Image or video file
   * @returns {Promise<Object>} Upload result with S3 URL
   */
  async uploadMedia(file) {
    if (!file) throw new Error('No file provided');
    if (file.size > 100 * 1024 * 1024) {
      throw new Error('File size exceeds 100MB limit');
    }

    let fileHash = null;
    try {
      fileHash = await this.calculateFileHash(file);
    } catch (hashError) {
      console.warn('Failed to calculate file hash, continuing without it:', hashError);
    }

    const formData = new FormData();
    formData.append('photo', file);
    if (fileHash) formData.append('fileHash', fileHash);

    try {
      const response = await api.post('/ai-analyses/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000 // 3 min — videos can be up to 100MB
      });
      if (!response.data?.success || !response.data?.data?.photoUrl) {
        throw new Error(response.data?.error || 'Upload succeeded but no media URL returned');
      }
      return response.data.data;
    } catch (error) {
      const errorData = error.response?.data;
      throw new Error(errorData?.error || errorData?.message || error.message || 'Failed to upload media');
    }
  }

  /**
   * Upload multiple photos
   * @param {File[]} files - Array of photo files
   * @returns {Promise<Object[]>} Array of upload results
   */
  async uploadMultiplePhotos(files) {
    const uploads = files.map(file => this.uploadPhoto(file));
    return Promise.all(uploads);
  }

  /**
   * Upload voice/audio file to S3
   * @param {File|Blob} audioFile - Audio file to upload
   * @returns {Promise<Object>} Upload result with S3 URL, hash, and deduplication info
   */
  async uploadVoice(audioFile) {
    try {
      // Calculate file hash for deduplication
      const fileHash = await this.calculateFileHash(audioFile);
      
      const formData = new FormData();
      formData.append('audio', audioFile);
      formData.append('fileHash', fileHash);

      const response = await api.post('/ai-analyses/upload-voice', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.data;
    } catch (error) {
      console.error('Error uploading voice file:', error);
      throw new Error(error.response?.data?.error || 'Failed to upload voice file');
    }
  }

  /**
   * Upload document file to S3
   * @param {File} documentFile - Document file to upload
   * @returns {Promise<Object>} Upload result with S3 URL, hash, and deduplication info
   */
  async uploadDocument(documentFile) {
    try {
      // Calculate file hash for deduplication
      const fileHash = await this.calculateFileHash(documentFile);
      
      const formData = new FormData();
      formData.append('document', documentFile);
      formData.append('fileHash', fileHash);

      const response = await api.post('/ai-analyses/upload-document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw new Error(error.response?.data?.error || 'Failed to upload document');
    }
  }
}

export default new PhotoUploadService();
